import { useEffect } from 'react'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Table, TableBody, TableCell, TableHeader, TableHeadV2, TableRowV2 } from 'src/components/molecules/Table'
import { useInView } from 'react-intersection-observer'
import { Loader } from 'lucide-react'
import { JumpingDots } from 'src/components/atoms/JumpingDots'

interface TeamMembersTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  onLoadMore?: () => void
}

function TeamMembersTable<TData, TValue>({
  columns,
  data,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
}: TeamMembersTableProps<TData, TValue>) {
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && onLoadMore) {
      onLoadMore()
    }
  }, [inView, hasNextPage, isFetchingNextPage, onLoadMore])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return <Loader className=" mx-auto h-6 w-6 animate-spin text-light-green-600" size={32} />
  }

  return (
    <div className="relative max-h-[600px] overflow-y-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-gray-800">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRowV2 key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeadV2 key={header.id}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHeadV2>
              ))}
            </TableRowV2>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRowV2 key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={(cell.column.columnDef.meta as { cellClassName?: string })?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRowV2>
            ))
          ) : (
            <TableRowV2>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRowV2>
          )}
        </TableBody>
      </Table>

      {/* Loader trigger */}
      {hasNextPage && inView && (
        <div ref={ref} className="h-20 w-full">
          {isFetchingNextPage && <JumpingDots className="text-light-green-50" />}
        </div>
      )}
    </div>
  )
}

export default TeamMembersTable
